import React, { forwardRef, useId } from 'react';
import cn from 'classnames';

export type MPFInputSize = 'xs' | 'sm' | 'md' | 'lg';

export interface MPFInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  helperText?: string;
  errorText?: string;
  size?: MPFInputSize;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  className?: string;
  inputClassName?: string;
}

export const MPFInput = forwardRef<HTMLInputElement, MPFInputProps>(
  function MPFInput(
    {
      label,
      helperText,
      errorText,
      size = 'md',
      leadingIcon,
      trailingIcon,
      required,
      id,
      className,
      inputClassName,
      ...rest
    },
    ref,
  ) {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const descriptionId = `${inputId}-description`;
    const isInvalid = Boolean(errorText);
    const description = errorText || helperText;

    const wrapperClasses = cn(
      'mpf-input',
      'mpf-input-bordered',
      'flex items-center gap-2',
      {
        'mpf-input-xs': size === 'xs',
        'mpf-input-sm': size === 'sm',
        'mpf-input-lg': size === 'lg',
        'mpf-input--error': isInvalid,
      },
      className,
    );

    return (
      <div className="mpf-form-control">
        {label && (
          <label htmlFor={inputId} className="mpf-label">
            <span className="mpf-label-text">
              {label}
              {required && <span aria-hidden="true"> *</span>}
            </span>
          </label>
        )}
        <div className={wrapperClasses}>
          {leadingIcon && (
            <span
              className="mpf-input__icon mpf-input__icon--leading"
              aria-hidden="true"
            >
              {leadingIcon}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            className={cn(
              'grow border-0 bg-transparent outline-none',
              inputClassName,
            )}
            required={required}
            aria-invalid={isInvalid || undefined}
            aria-required={required || undefined}
            aria-describedby={description ? descriptionId : undefined}
            {...rest}
          />
          {trailingIcon && (
            <span
              className="mpf-input__icon mpf-input__icon--trailing"
              aria-hidden="true"
            >
              {trailingIcon}
            </span>
          )}
        </div>
        {description && (
          <span
            id={descriptionId}
            className={cn(
              'mpf-label-text-alt',
              isInvalid ? 'text-error' : 'text-on-surface-low',
            )}
          >
            {description}
          </span>
        )}
      </div>
    );
  },
);

export default MPFInput;
